import { Breakpoint } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';

import dayjs, { Dayjs } from 'dayjs';
import { IntlShape } from 'node_modules/@umijs/plugins/node_modules/react-intl/lib/react-intl';

import { ChartData, FindChartsResponse } from '@/types';

export * from './clarity';
export * from './pickerFormat';
export * from './staticIntl';

export const getDaysBetween = (start: Dayjs, end: Dayjs) => {
  const range = [];
  let current = start;
  while (!current.isAfter(end)) {
    range.push(current);
    current = current.add(1, 'days');
  }
  return range;
};

export const formatDateTime = (intl: IntlShape, date: string): string => intl.formatDate(date, { day: '2-digit', hour: '2-digit', minute: '2-digit', month: '2-digit', year: 'numeric' });

export const formatDate = (intl: IntlShape, date: string): string => intl.formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });

export const formatTime = (intl: IntlShape, date: string): string => intl.formatDate(date, { hour: '2-digit', minute: '2-digit' });

export const formatDateTimeSecond = (intl: IntlShape, date: string): string => intl.formatDate(date, { day: '2-digit', hour: '2-digit', minute: '2-digit', month: '2-digit', second: '2-digit', year: 'numeric' });

export const isDateAfterToday: RangePickerProps['disabledDate'] = current => current > dayjs().endOf('day');

interface EChartsRef {
  getEchartsInstance: () => { getDataURL: (opts?: { backgroundColor?: string; pixelRatio?: number }) => string };
}

export const handleExportChart = (
  chartRef: React.MutableRefObject<EChartsRef | null>,
  setSelectedCharts: React.Dispatch<React.SetStateAction<ChartData[]>>,
  filters: Omit<ChartData, 'chart'>,
) => {
  if (chartRef.current) {
    const chartDataUrl = chartRef.current.getEchartsInstance().getDataURL({
      backgroundColor: '#fff',
      pixelRatio: 2,
    });
    setSelectedCharts(prev => ([...prev, { chart: chartDataUrl, ...filters }]));
  }
};

const FALLBACK_CHART_COLOR = '#8c8c8c';
const DEFAULT_DEFECT_COLORS: Record<string, string | undefined> = { no_defect: 'rgb(51, 160, 44)' };

const resolveSeriesColor = (name: string, colorMap: Map<string, null | string>) => colorMap.get(name) ?? DEFAULT_DEFECT_COLORS[name] ?? FALLBACK_CHART_COLOR;

export interface EChartsTooltipParam {
  axisValue?: string;
  marker?: string;
  seriesName: string;
  value: null | number;
}

export interface EChartsTimeSeriesParam {
  marker?: string;
  seriesName: string;
  value?: [number, null | number];
}

export interface PivotedChartSeries {
  color: string;
  data: (null | number)[];
  name: string;
}

export interface PivotedChartData {
  categories: string[];
  series: PivotedChartSeries[];
}

export const pivotChartData = (
  data: { [key: string]: unknown; name: string; value: null | number | string }[],
  categoryField: string,
  colors?: (null | string)[][],
): PivotedChartData => {
  const colorMap = new Map((colors ?? []).map(([n, c]) => [n ?? '', c ?? null]));
  const categories = [...new Set(data.map(d => String(d[categoryField])))].filter(Boolean);
  const names = [...new Set(data.map(d => d.name))];

  return {
    categories,
    series: names.map(name => ({
      color: resolveSeriesColor(name, colorMap),
      data: categories.map((cat) => {
        const item = data.find(d => d.name === name && String(d[categoryField]) === cat);
        const raw = item?.value ?? null;
        return raw === null ? null : Number(raw);
      }),
      name,
    })),
  };
};

export interface TimeSeriesEntry {
  color: string;
  data: [string, null | number][];
  name: string;
}

export const pivotToTimeSeries = (
  data: { name: string; timestamp: string; value: null | number | string }[],
  colors?: (null | string)[][],
): TimeSeriesEntry[] => {
  const colorMap = new Map((colors ?? []).map(([n, c]) => [n ?? '', c ?? null]));
  const names = [...new Set(data.map(d => d.name))];

  return names.map(name => ({
    color: resolveSeriesColor(name, colorMap),
    data: data.filter(d => d.name === name).map((d) => {
      const raw = d.value;
      return [d.timestamp, raw === null ? null : Number(raw)] as [string, null | number];
    }),
    name,
  }));
};

export const remapTimesChartData = (chartData: FindChartsResponse[], unit: string) => chartData.flatMap((item) => {
  const items = Object.keys(item).filter(key => key.includes(unit));

  return items.map(field => ({
    name: field.replace(unit, ''),
    timestamp: String(item.timestamp),
    value: item[field],
  }));
});

export const calculateImageCardWidth = (containerWidth: number | undefined, breakpoint: Breakpoint | undefined, gap: number) => {
  const columns: Record<Breakpoint, number> = { lg: 3, md: 3, sm: 2, xl: 4, xs: 1, xxl: 5 };
  return containerWidth && breakpoint ? (containerWidth / columns[breakpoint] - gap) : 200;
};
